import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directory, isForbidden } from '@cwos/api-client';
import type { messaging } from '@cwos/api-client';
import { ConfirmAction, Pill, SkeletonSet } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { errorMessage, useAddMember, useRemoveMember } from './messaging.js';

type Member = messaging.ChannelMemberView;

/**
 * Members roster + management. The list is always shown (identity, never a
 * UUID). Add/remove controls render ONLY when the actor holds `messaging:manage`
 * — the same permission the server checks (routes.ts); it is not a per-channel
 * role, so this must not be gated on channel membership `role`. A backend
 * refusal — 403 for the permission, 404 for a conversation this account cannot
 * reach at all — still surfaces honestly regardless, through `errorMessage`,
 * which is why the server's not-found sentence is written for a reader rather
 * than being `channel not found: <uuid>`.
 */
export function MemberManager({
  channelId,
  members,
  canManage,
}: {
  channelId: string;
  members: Member[];
  canManage: boolean;
}) {
  const [adding, setAdding] = useState(false);

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">Members</h2>
        {canManage ? (
          <button type="button" className="rn-link" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Close' : 'Add member'}
          </button>
        ) : null}
      </div>

      {canManage && adding ? (
        <AddMemberPicker channelId={channelId} members={members} onDone={() => setAdding(false)} />
      ) : null}

      <ul className="msg-member-list">
        {members.map((m) => (
          <MemberRow key={m.userId} channelId={channelId} member={m} canManage={canManage} />
        ))}
      </ul>
    </section>
  );
}

function MemberRow({
  channelId,
  member,
  canManage,
}: {
  channelId: string;
  member: Member;
  canManage: boolean;
}) {
  const remove = useRemoveMember(channelId);
  const forbidden = remove.isError && isForbidden(remove.error);
  // PH-UX-004: removing somebody from a conversation cuts their access to it —
  // one click, one row, and the rows are adjacent. Per-row component, so a
  // plain boolean is already scoped to this member.
  const [confirmRemove, setConfirmRemove] = useState(false);
  return (
    <li className="msg-member">
      <span className="msg-member-main">
        <span className="msg-member-name">{member.userName}</span>
        <span className="rn-muted">{member.userRole}</span>
      </span>
      {member.role === 'owner' ? <Pill variant="sky">Owner</Pill> : null}
      {canManage && member.role !== 'owner' ? (
        <button
          type="button"
          className="rn-link"
          disabled={remove.isPending}
          onClick={() => setConfirmRemove(true)}
        >
          {remove.isPending ? 'Removing…' : 'Remove'}
        </button>
      ) : null}
      <ConfirmAction
        open={confirmRemove}
        title={`Remove ${member.userName} from this conversation?`}
        consequence={
          `${member.userName} loses access to this conversation straight away, including the ` +
          `messages already in it. Nothing they have written is deleted — it stays in the ` +
          `thread for everyone still in it. Adding them back later does not re-notify them of ` +
          `what they missed.`
        }
        confirmLabel="Remove from conversation"
        pendingLabel="Removing…"
        pending={remove.isPending}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => remove.mutate(member.userId)}
      />
      {forbidden ? (
        <span className="msg-error">Not allowed</span>
      ) : remove.isError ? (
        <span className="msg-error">{errorMessage(remove.error, 'Could not remove them.')}</span>
      ) : null}
    </li>
  );
}

function AddMemberPicker({
  channelId,
  members,
  onDone,
}: {
  channelId: string;
  members: Member[];
  onDone: () => void;
}) {
  const add = useAddMember(channelId);
  // The people picker is the staff roster, so it answers to the roster's keys.
  // This query carried no `enabled:` at all and fired for any authenticated
  // caller who opened the panel; without `user:read` the server now refuses it.
  // At ORGANIZATION scope, which is where the route decides it: both of its
  // gates are `can()` with no scope argument, so a `user:read` confined to one
  // program is a 403 rather than a shorter roster.
  const permissions = usePermissions();
  const mayList = mayListWorkers(permissions);
  // Default-deny is right for `enabled:` and wrong for the sentence below: it
  // is also false while the set is loading and after it fails, and neither of
  // those is a fact about this reader's permissions. Only a resolved set is.
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !mayList;
  const roster = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: mayList,
  });

  const existing = useMemo(() => new Set(members.map((m) => m.userId)), [members]);
  const candidates = useMemo(
    () => (roster.data ?? []).filter((w) => w.userId && !existing.has(w.userId)),
    [roster.data, existing],
  );

  const forbidden = add.isError && isForbidden(add.error);

  return (
    <div className="msg-add">
      {rosterWithheld ? (
        // The query is DISABLED without `user:read`, and react-query reports a
        // disabled query as neither loading nor errored with no data — which
        // fell through `?? []` to "Everyone with an account is already here."
        // A person who may not be told who works here was being told that
        // everyone already had.
        <p className="rn-muted" role="status">
          Naming the people in your organization needs the directory permission, so we cannot show
          you who is available to add. This is not an empty directory.
        </p>
      ) : !mayList || roster.isLoading ? (
        <SkeletonSet surface="table" rows={4} />
      ) : roster.isError && roster.data === undefined ? (
        // "Everyone with an account is already here" is a claim about the whole
        // directory. It was being made from a directory read that failed.
        <p className="msg-error" role="status">
          We couldn’t load the directory, so we cannot say who is available to add.{' '}
          <button type="button" className="rn-link" onClick={() => void roster.refetch()}>
            Try again
          </button>
        </p>
      ) : candidates.length === 0 ? (
        <p className="rn-muted">Everyone with an account is already here.</p>
      ) : (
        <ul className="msg-people">
          {candidates.map((w) => (
            <li key={w.id}>
              <button
                type="button"
                className="msg-add-btn"
                disabled={add.isPending}
                onClick={() =>
                  add.mutate({ userId: w.userId as string }, { onSuccess: () => onDone() })
                }
              >
                <span className="msg-person-name">{w.displayName}</span>
                {w.roleTitle ? <span className="rn-muted">{w.roleTitle}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {forbidden ? (
        <p className="msg-error">You don’t have permission to manage members.</p>
      ) : add.isError ? (
        <p className="msg-error">
          {errorMessage(add.error, 'We couldn’t add that person just now.')}
        </p>
      ) : null}
    </div>
  );
}
